import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Eye, Download, Users, ChevronDown } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist";
import axiosInstance from "../../api/axiosInstance";
import { useApp } from "../../context/formflow/AppContext";
import { ROLE_PALETTE } from "../../constants/formflow/fieldTypes";
import { getFileUrl } from "../../utils/formflow/helpers";

pdfjsLib.GlobalWorkerOptions.workerSrc = "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js";

const num = (v) => parseFloat(v) || 0;

function getRoleColor(roles, roleId) {
  const idx = roles.findIndex((r) => r.id === roleId);
  return ROLE_PALETTE[Math.max(0, idx) % ROLE_PALETTE.length];
}

// Overlay component – now memoized to avoid re-renders
const ViewFieldOverlay = ({ field, value, roleColor }) => {
  if (!value) return null;

  let displayValue = value;
  if (field.type === "checkbox") {
    if (value === true || value === "true") displayValue = "✓";
    else return null;
  } else if (field.type === "signature" && typeof value === "string" && value.startsWith("data:")) {
    displayValue = "[Signature]";
  } else if (field.type === "date") {
    displayValue = new Date(value).toLocaleDateString();
  }

  return (
    <div
      className="absolute overflow-hidden rounded-sm pointer-events-none"
      style={{
        left: `${num(field.x)}%`,
        top: `${num(field.y)}%`,
        width: `${num(field.w)}%`,
        height: `${num(field.h)}%`,
        border: `1.5px solid ${roleColor.border}`,
        backgroundColor: `${roleColor.bg}cc`,
        zIndex: 5,
      }}
    >
      <div className="w-full h-full flex items-center justify-center text-xs font-medium text-gray-800 p-1 truncate">{displayValue}</div>
    </div>
  );
};

export function EnvelopeViewPage() {
  const { envelopeId } = useParams();
  const navigate = useNavigate();
  const { showToast } = useApp();

  const [loading, setLoading] = useState(true);
  const [envelopeData, setEnvelopeData] = useState(null);
  const [pages, setPages] = useState([]);
  const [selectedSignerId, setSelectedSignerId] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [expandedRoles, setExpandedRoles] = useState({});
  const pdfScrollRef = useRef(null);

  // Fetch envelope data
  useEffect(() => {
    const fetchEnvelope = async () => {
      try {
        const { data } = await axiosInstance.get(`/api/formflow/envelopes/${envelopeId}`);
        if (data.success) {
          setEnvelopeData(data.envelope);
          if (data.envelope.signers?.length) {
            setSelectedSignerId(data.envelope.signers[0].id);
          }
          if (data.envelope.file?.public_url) {
            await loadPdfPages(getFileUrl(data.envelope.file.public_url));
          }
        } else {
          showToast("Envelope not found", "error");
          navigate("/formflow/submissions");
        }
      } catch (err) {
        console.error(err);
        showToast("Failed to load envelope", "error");
      } finally {
        setLoading(false);
      }
    };
    fetchEnvelope();
  }, [envelopeId]);

  const loadPdfPages = async (url) => {
    try {
      const pdf = await pdfjsLib.getDocument(url).promise;
      const imgs = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const vp = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement("canvas");
        canvas.width = vp.width;
        canvas.height = vp.height;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
        imgs.push(canvas.toDataURL());
      }
      setPages(imgs);
    } catch (err) {
      console.error("PDF render error", err);
      showToast("Failed to render PDF", "error");
    }
  };

  const downloadSignerPdf = async (signerId) => {
    setDownloading(true);
    try {
      const response = await axiosInstance.get(`/api/formflow/envelopes/${envelopeId}/signer/${signerId}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(response.data);
      window.open(url, "_blank");
      URL.revokeObjectURL(url);
      showToast("PDF generated", "success");
    } catch (err) {
      console.error(err);
      showToast("Failed to generate PDF", "error");
    } finally {
      setDownloading(false);
    }
  };

  const toggleRole = (roleId) => {
    setExpandedRoles((prev) => ({ ...prev, [roleId]: !prev[roleId] }));
  };

  // Memoized data for faster rendering
  const { envelope, form, roles, signers, signersByRole, selectedSigner } = useMemo(() => {
    if (!envelopeData) return {};
    const { envelope, form, roles, signers } = envelopeData;
    const signersByRole = {};
    roles.forEach((role) => {
      signersByRole[role.id] = [];
    });
    signers.forEach((signer) => {
      if (signersByRole[signer.roleId]) signersByRole[signer.roleId].push(signer);
    });
    const selectedSigner = signers.find((s) => s.id === selectedSignerId);
    return { envelope, form, roles, signers, signersByRole, selectedSigner };
  }, [envelopeData, selectedSignerId]);

  // Prepare overlays for the selected signer – grouped by page
  const overlaysByPage = useMemo(() => {
    if (!selectedSigner || !roles || pages.length === 0) return {};
    const map = {};
    roles.forEach((role) => {
      const signerForRole = signers?.find((s) => s.roleId === role.id && s.id === selectedSigner.id);
      if (!signerForRole) return;
      const roleCol = getRoleColor(roles, role.id);
      role.fields.forEach((field) => {
        const value = signerForRole.fieldValues?.find((fv) => fv.fieldId === field.id)?.value;
        if (!value) return;
        if (!map[field.pageIndex]) map[field.pageIndex] = [];
        map[field.pageIndex].push({ field, value, roleColor: roleCol });
      });
    });
    return map;
  }, [selectedSigner, roles, signers, pages]);

  if (loading) return <div className="min-h-screen bg-slate-50 p-8 text-center">Loading envelope...</div>;
  if (!envelopeData) return null;

  const statusColors = {
    draft: "bg-gray-100 text-gray-700",
    sent: "bg-blue-100 text-blue-700",
    viewed: "bg-yellow-100 text-yellow-700",
    partially_signed: "bg-orange-100 text-orange-700",
    completed: "bg-green-100 text-green-700",
    declined: "bg-red-100 text-red-700",
  };

  return (
    <div className="bg-slate-50 min-h-screen">
      <div className="flex flex-col max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white border-b px-6 py-3 flex items-center gap-4 sticky top-0 z-10">
          <button onClick={() => navigate(-1)} className="text-gray-500 hover:text-gray-700">
            <ArrowLeft size={18} />
          </button>
          <div className="flex-1">
            <div className="text-xs text-gray-400">{form?.name}</div>
            <h2 className="text-lg font-bold">Envelope #{envelope.id}</h2>
          </div>
          <div className="text-sm text-gray-500">Status: {envelope.status}</div>
        </div>

        <div className="flex gap-6 p-6">
          {/* PDF Viewer */}
          <div ref={pdfScrollRef} className="flex-1 bg-white shadow-lg overflow-y-auto rounded-lg max-h-[88vh]">
            {pages.length > 0 ? (
              pages.map((src, pageIndex) => (
                <div key={pageIndex} className="relative mb-5">
                  <img src={src} alt={`Page ${pageIndex + 1}`} className="w-full block" />
                  {(overlaysByPage[pageIndex] || []).map((overlay, idx) => (
                    <ViewFieldOverlay key={`${overlay.field.id}-${idx}`} field={overlay.field} value={overlay.value} roleColor={overlay.roleColor} />
                  ))}
                </div>
              ))
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-400">Loading PDF preview...</div>
            )}
          </div>

          {/* Sidebar */}
          <div className="w-80 shrink-0 flex flex-col gap-4">
            <div className="bg-white rounded-xl shadow p-4">
              <h3 className="font-bold text-sm mb-2">Envelope</h3>
              <p className="text-xs text-gray-600 mb-1">Created: {new Date(envelope.created_at).toLocaleString()}</p>
              <p className="text-xs text-gray-600">
                Status: <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${statusColors[envelope.status] || "bg-gray-100"}`}>{envelope.status}</span>
              </p>
            </div>

            {/* Roles and signers */}
            <div className="bg-white rounded-xl shadow p-4">
              <h3 className="font-bold text-sm mb-3 flex items-center gap-2">
                <Users size={14} /> Signers by Role
              </h3>
              <div className="space-y-2 max-h-[60vh] overflow-y-auto">
                {roles.map((role) => {
                  const roleSigners = signersByRole[role.id] || [];
                  const isExpanded = expandedRoles[role.id];
                  const roleCol = getRoleColor(roles, role.id);
                  return (
                    <div key={role.id} className="border border-gray-100 rounded-lg">
                      <button onClick={() => toggleRole(role.id)} className="w-full flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ background: roleCol.dot }} />
                          <span className="text-sm font-semibold">{role.name}</span>
                          <span className="text-xs text-gray-400">({roleSigners.length})</span>
                        </div>
                        <ChevronDown size={14} className={`transform transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                      </button>
                      {isExpanded && (
                        <div className="pl-4 pb-2 space-y-1">
                          {roleSigners.map((signer) => {
                            const isSelected = signer.id === selectedSignerId;
                            const isCompleted = signer.status === "completed" || signer.status === "signed";
                            const isViewed = signer.status === "viewed";
                            return (
                              <div key={signer.id} className="flex items-center justify-between p-1">
                                <button onClick={() => setSelectedSignerId(signer.id)} className={`flex-1 text-left text-xs p-1 rounded ${isSelected ? "bg-red-50 text-red-700 font-semibold" : "hover:bg-gray-50"}`}>
                                  <div>{signer.name}</div>
                                  <div className="text-gray-400 text-[10px]">{signer.email}</div>
                                </button>
                                <div className="flex items-center gap-1">
                                  {isCompleted && <Check size={12} className="text-green-500" />}
                                  {isViewed && !isCompleted && <Eye size={12} className="text-yellow-500" />}
                                  <button onClick={() => downloadSignerPdf(signer.id)} disabled={downloading} className="p-1 text-blue-600 hover:bg-blue-50 rounded" title="Download this signer's PDF">
                                    <Download size={12} />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Selected signer's filled values */}
            {selectedSigner && (
              <div className="bg-white rounded-xl shadow p-4">
                <h3 className="font-bold text-sm mb-2">Filled Values</h3>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {selectedSigner.fieldValues?.map((fv) => {
                    const field = roles.flatMap((r) => r.fields).find((f) => f.id === fv.fieldId);
                    if (!field) return null;
                    let display = fv.value;
                    if (field.type === "checkbox") display = fv.value === true || fv.value === "true" ? "Yes" : "No";
                    else if (field.type === "signature" && typeof fv.value === "string" && fv.value.startsWith("data:")) display = "[Signature]";
                    else if (field.type === "date") display = new Date(fv.value).toLocaleDateString();
                    return (
                      <div key={fv.id} className="border-b border-gray-100 pb-1">
                        <div className="text-xs font-semibold text-gray-600">{field.label}</div>
                        <div className="text-xs text-gray-800 wrap-break-word">{display}</div>
                      </div>
                    );
                  })}
                  {!selectedSigner.fieldValues?.length && <p className="text-xs text-gray-400">No fields filled yet</p>}
                </div>
                <button onClick={() => downloadSignerPdf(selectedSigner.id)} disabled={downloading} className="mt-3 w-full py-2 bg-red-600 text-white rounded-lg text-sm font-semibold flex items-center justify-center gap-2 hover:bg-red-700 disabled:opacity-50">
                  {downloading ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Download size={14} />}
                  Download PDF for {selectedSigner.name}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
